import { publicHeaderRequest, publicRequest } from "../requestMethods/requestMethods";

/**
 * @typedef {Object} UploadMediaPayload
 * @property {File} file
 * @property {string} mediaType
 * @property {string} [folderName]
 * @property {number} [duration]
 * @property {string} [seriesId]
 * @property {string} [description]
 * @property {string[] | string} [tags]
 * @property {string[] | string} [allowedSubscriptions]
 */

const normalizeArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

/**
 * @param {UploadMediaPayload} params
 */
async function uploadMedia({
  file,
  mediaType,
  folderName,
  duration,
  seriesId,
  description,
  tags,
  allowedSubscriptions,
}) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("mediaType", String(mediaType || "").toUpperCase());

  if (folderName) {
    formData.append("folderName", folderName);
  }

  const normalizedDuration = Number(duration);
  if (Number.isFinite(normalizedDuration) && normalizedDuration > 0) {
    formData.append("duration", String(normalizedDuration));
  }

  if (seriesId) {
    formData.append("seriesId", String(seriesId));
  }

  if (description) {
    formData.append("description", String(description));
  }

  normalizeArray(tags).forEach((tag) => {
    formData.append("tags", tag);
  });

  normalizeArray(allowedSubscriptions).forEach((subscription) => {
    formData.append("allowedSubscriptions", subscription);
  });

  return await publicHeaderRequest.post("/media/upload", formData);
}

async function getMediaById(mediaId) {
  return await publicRequest.get(`/media/${mediaId}`);
}

async function getAllMedia() {
  return await publicRequest.get("/media");
}

async function getMediaByType(type) {
  return await publicRequest.get(`/media/type/${String(type || "").toUpperCase()}`);
}

async function getMediaByDuration(duration) {
  return await publicRequest.get(`/media/by-duration/${duration}`);
}

async function getMediaByDurationRange(minDuration, maxDuration) {
  return await publicRequest.get("/media/by-duration-range", {
    params: {
      minDuration,
      maxDuration,
    },
  });
}

async function getMediaByTag(tag) {
  return await publicRequest.get("/media/by-tag", {
    params: { tag },
  });
}

async function getMediaByAllowedSubscription(subscription) {
  return await publicRequest.get(`/media/allowedSubscription/${subscription}`);
}

async function updateMediaById(mediaId, payload) {
  if (!mediaId) {
    throw new Error('Falta mediaId para actualizar.');
  }

  try {
    return await publicRequest.put(`/media/update/${mediaId}`, payload);
  } catch (error) {
    if (error?.response?.status === 404) {
      return await publicRequest.put(`/media/${mediaId}`, payload);
    }

    throw error;
  }
}

async function removeMediaById(mediaId) {
  return await publicRequest.delete(`/media/${mediaId}`);
}

export {
  uploadMedia,
  getMediaById,
  getAllMedia,
  getMediaByType,
  getMediaByDuration,
  getMediaByDurationRange,
  getMediaByTag,
  getMediaByAllowedSubscription,
  updateMediaById,
  removeMediaById,
};
